import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import {
  apiRequest,
  saveToken,
  SuccessToast,
  ErrorToast,
  toastConfig,
} from "@ridecloud/shared";
import { LoaderInside } from "../components/Loader";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ArrowLeft, LogIn, Shield, Mail, Lock } from "lucide-react";
import { devError } from "@ridecloud/shared";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post("/auth/login", { email, password })
      );
      await saveToken(res.data.token);
      SuccessToast("Logged in successfully");
      navigate("/dashboard");
    } catch (err) {
      ErrorToast(err.response?.data?.message || "Server error");
      devError("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-green-50 to-green-100 flex items-center justify-center relative overflow-hidden px-4">
      {/* Blurry gradient blobs like homepage */}
      <div className="absolute w-96 h-96 -top-32 -left-32 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full opacity-20 blur-3xl animate-pulse" />
      <div className="absolute w-80 h-80 bottom-0 -right-20 bg-gradient-to-br from-teal-400 to-green-500 rounded-full opacity-20 blur-3xl animate-pulse delay-1000" />

      {/* Card */}
      <div className="relative z-10 w-full max-w-md bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-emerald-100 animate-fade-in-up">
        {/* Back Button */}
        <button
          onClick={() => navigate("/")}
          className="absolute top-4 left-4 p-2 rounded-xl hover:bg-emerald-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-emerald-600" />
        </button>

        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
            <img
              src="/img/ride_cloud.png"
              alt="RideCloud"
              className="w-12 h-12"
            />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-emerald-500 to-teal-600 bg-clip-text text-transparent">
            Welcome Back
          </h2>
          <p className="text-gray-600 mt-2">
            Please enter your details to sign in
          </p>
        </div>

        <Toaster {...toastConfig} />

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email */}
          <div className="relative">
            <Mail className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full pl-12 pr-4 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
          </div>

          {/* Password */}
          <div className="relative">
            <Lock className="absolute left-4 top-3.5 w-5 h-5 text-emerald-500" />
            <input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setIsFocused(true)}
              required
              className="w-full pl-12 pr-12 py-3 rounded-2xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 outline-none transition-all"
            />
            {isFocused && password.length > 0 && (
              <span
                className="absolute right-4 top-3.5 cursor-pointer text-gray-500 hover:text-emerald-600 transition-colors"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <VisibilityOff fontSize="small" />
                ) : (
                  <Visibility fontSize="small" />
                )}
              </span>
            )}
          </div>

          {/* Forgot password */}
          <div className="text-right">
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-emerald-600 hover:underline"
            >
              Forgot your password?
            </Link>
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center items-center gap-2 py-3 rounded-2xl font-semibold text-white bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg hover:shadow-emerald-500/30 hover:scale-105 transition-all duration-300"
          >
            {loading ? <LoaderInside /> : <LogIn className="w-5 h-5" />}
            Sign In
          </button>
        </form>

        {/* OR */}
        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-gray-400 text-sm font-medium">OR</span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* OTP login */}
        <Link
          to="/otp-login"
          className="block w-full text-center py-3 rounded-2xl font-semibold border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 transition-all"
        >
          Login with OTP
        </Link>

        {/* Footer */}
        <p className="text-center mt-6 text-gray-600 text-sm">
          New to RideCloud?{" "}
          <Link
            to="/signup"
            className="font-semibold text-emerald-600 hover:underline"
          >
            Sign Up
          </Link>
        </p>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
